import React from 'react';
import cx from 'classnames';
import dynamic from 'next/dynamic';

import { useVehiclePhotosByKind } from 'Components/Gallery';
import type { GalleryImageWithTitle } from 'Components/Gallery/MainGallery/MainGallery';
import { useGalleryTrigger } from 'Components/pages/VehicleDetailsRedesign/Components/GalleryProvider';
import type { IMAGE_KIND } from 'Components/pages/VehicleDetailsRedesign/VehiclePhotos.const';
import BasicThumbnail from 'Components/Thumbnail/BasicThumbnail';
import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import type { YesNoDash } from 'Pages/vehicles/helpers/vehicle.schema';
import { triggerSectionImageClicked } from 'Services/analytics/events';
import { useTrackEventWhenReady } from 'Services/optimizely/events/useTrackEventWhenReady';
import { OPTIMIZELY_EVENTS } from 'Services/optimizely/optimizely.consts';
import { useOptimizelyDealerEnquiryCombinedId } from 'Services/optimizely/useOptimizelyDealerEnquiryCombinedId';
import { toUpperCaseFirst } from 'Utilities/formatters';

import { isMobilizeVehicle } from '../../helpers/isMobilizeVehicle';

import styles from './DamageWithImage.module.scss';

type KindValues = (typeof IMAGE_KIND)[keyof typeof IMAGE_KIND];

interface DamageWithImageProps {
	damageDescription: Nullable<string>;
	hasDamage: YesNoDash;
	kind: KindValues;
}

const DynamicDamageLocationImg = dynamic(
	() => import('@motorway/motorway-storybook-cra').then((module) => module.DamageLocationImg),
	{
		ssr: false,
	},
);

interface DamagePhotosProps {
	image: GalleryImageWithTitle;
	kind: KindValues;
}

const DamagePhotos: React.FC<DamagePhotosProps> = ({ image, kind }) => {
	const openGallery = useGalleryTrigger();
	const trackingId = useOptimizelyDealerEnquiryCombinedId();
	const track = useTrackEventWhenReady(OPTIMIZELY_EVENTS.CONDITION_AND_DAMAGE_PHOTO_CLICKED);
	const vehicle = useVehicleData();

	const handleOnClick = (id: number) => {
		track({ attributes: { $opt_bucketing_id: null }, overrideUserId: trackingId });
		openGallery({ id });
		triggerSectionImageClicked({ imageKind: kind });
	};

	const isMobilize = isMobilizeVehicle(vehicle.sourcePartner);
	const hasDamageMeta = !isMobilize && image && 'damageMeta' in image && !!image.damageMeta;

	const ariaLabel = kind.charAt(0).toUpperCase() + kind.slice(1).replace(/_/g, ' ');
	return (
		<div className={styles.damagePhotosContainer}>
			<div className={cx(styles.damageLocation, { [styles.damageLocationHasDamage]: hasDamageMeta })}>
				{hasDamageMeta ? (
					<DynamicDamageLocationImg
						damageMeta={image.damageMeta as DamageLocationImgProps['damageMeta']}
						imgProps={{
							'aria-label': `${ariaLabel} location`,
							className: styles.test,
						}}
						kind={kind}
						photoAspectContainerProps={{
							className: styles.damageLocationThumb,
							onClick: () => handleOnClick(image.id),
						}}
					/>
				) : null}
			</div>
			<div className={styles.damagePhoto}>
				<BasicThumbnail
					key={image.id}
					authenticated
					alt={image.kind}
					className={styles.thumb}
					height={279}
					image={{
						presets: image.presetsArray,
						url: image.url,
					}}
					imgAttributes={{ 'aria-label': ariaLabel }}
					onClick={() => handleOnClick(image.id)}
					rootComponent={'DamagePhotos'}
					width={372}
				/>
			</div>
		</div>
	);
};

export const DamageWithImage: React.FC<DamageWithImageProps> = ({ damageDescription, hasDamage, kind }) => {
	const images = useVehiclePhotosByKind([kind]);

	const isNoDamage = hasDamage === 'No' || hasDamage === '-';
	const shouldUseHasDamageValue = isNoDamage || damageDescription === null || damageDescription === '';
	const description = shouldUseHasDamageValue ? hasDamage : toUpperCaseFirst(damageDescription.trim());
	const hasImages = images.length > 0;

	return (
		<div className={styles.damageInfoWithPhotos}>
			<p className={cx(styles.description, { [styles.removeDamagePadding]: !hasImages })}>{description}</p>
			{hasDamage === 'Yes' && hasImages && (
				<div className={styles.photos}>
					{images.map((image) => (
						<DamagePhotos key={image.id} image={image} kind={kind} />
					))}
				</div>
			)}
		</div>
	);
};
